import { useCallback, useState } from 'react';

/**
 * The demo (SAMPLE) evidence dataset — and the honesty rules that bind it.
 *
 * A first-run workspace holds nothing, so there is nothing to show an operator
 * learning what the Evidence module does. This provides a "load sample data"
 * action that populates the screen with a handful of illustrative records —
 * WITHOUT ever writing them to the evidence service.
 *
 * The line that must never move: **sample data is never presentable as real
 * evidence.** So this dataset is:
 *
 * - **Local only.** It lives in `localStorage`, is scoped to the org, and is
 *   never sent to the evidence service, never ingested, never committed. A
 *   sample record therefore has no place in the transparency log, no chain of
 *   custody, and no verification bundle — because it is an illustration, not a
 *   held object. The real asset list and the sample list are drawn from two
 *   different sources and rendered in two different, clearly-labelled sections;
 *   they cannot be confused because they never share a table.
 * - **Honestly graded.** Every record carries `provenance_grade: 'declared'`
 *   and a `sample` state. Nothing here is ever labelled "captured", "committed",
 *   "stored" or "verified" — those are claims only a real held asset can make.
 * - **Marked, everywhere.** Every record carries `demo: true`; the UI renders a
 *   persistent SAMPLE marker on the banner, on every row and on any detail view.
 * - **Trivially clearable.** One control removes the whole set, and clearing it
 *   touches nothing real because nothing real was ever created.
 */

const KEY = (orgId: string) => `evac.evidence.demo.${orgId}`;

export interface DemoAsset {
  /** Present on every sample record, and only on sample records. */
  demo: true;
  id: string;
  /** Deliberately not one of the real `EvidenceAsset` states. */
  state: 'sample';
  media_type: string;
  bytes: number;
  /**
   * A stand-in string, never a real content hash — labelled `sample:` so it can
   * never read as a Merkle root a defendant could check.
   */
  content_ref: string;
  captured_at: string;
  /** Honest grade: a browser/declared upload, never device-attested capture. */
  provenance_grade: 'declared';
  /** One line saying what this record is here to illustrate. */
  illustrates: string;
}

/** When the sample set was loaded for this org, or null if it is not loaded. */
export function demoLoadedAt(orgId: string): number | null {
  try {
    const raw = localStorage.getItem(KEY(orgId));
    if (!raw) return null;
    const at = Number(JSON.parse(raw)?.loadedAt);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

/** Load the sample set for this org. Records the load time so timestamps read sensibly. */
export function loadDemoEvidence(orgId: string): void {
  try {
    localStorage.setItem(KEY(orgId), JSON.stringify({ loadedAt: Date.now() }));
  } catch {
    // Storage can throw (private window, blocked site data). The sample set is
    // a convenience; losing the ability to persist it is not worth a crash.
  }
}

/** Remove the sample set. Touches nothing real, because nothing real was created. */
export function clearDemoEvidence(orgId: string): void {
  try {
    localStorage.removeItem(KEY(orgId));
  } catch {
    // See loadDemoEvidence.
  }
}

const MINUTE = 60_000;

/**
 * The sample records, computed relative to when the set was loaded so the
 * "captured" times read as a plausible recent afternoon rather than a frozen
 * date. Deterministic given `loadedAt`.
 */
export function demoAssets(loadedAt: number): DemoAsset[] {
  const at = (minsAgo: number) => new Date(loadedAt - minsAgo * MINUTE).toISOString();
  return [
    {
      demo: true,
      id: 'demo-0000-0000-0000-000000000001',
      state: 'sample',
      media_type: 'video/mp4',
      bytes: 24_117_248,
      content_ref: 'sample:2f9a…not-a-real-hash',
      captured_at: at(112),
      provenance_grade: 'declared',
      illustrates:
        'A browser recording — declared provenance, the honest grade for an unsigned upload.',
    },
    {
      demo: true,
      id: 'demo-0000-0000-0000-000000000002',
      state: 'sample',
      media_type: 'image/jpeg',
      bytes: 3_355_443,
      content_ref: 'sample:8c41…not-a-real-hash',
      captured_at: at(47),
      provenance_grade: 'declared',
      illustrates:
        'A still image uploaded with a stated provenance, as a third party would supply it.',
    },
    {
      demo: true,
      id: 'demo-0000-0000-0000-000000000003',
      state: 'sample',
      media_type: 'application/pdf',
      bytes: 262_144,
      content_ref: 'sample:d0e7…not-a-real-hash',
      captured_at: at(9),
      provenance_grade: 'declared',
      illustrates: 'A document, to show that evidence is not only recordings.',
    },
  ];
}

export interface DemoEvidence {
  loaded: boolean;
  loadedAt: number | null;
  assets: DemoAsset[];
  load: () => void;
  clear: () => void;
}

/** React binding over the sample set, re-rendering on load and clear. */
export function useDemoEvidence(orgId: string): DemoEvidence {
  const [loadedAt, setLoadedAt] = useState<number | null>(() => demoLoadedAt(orgId));

  const load = useCallback(() => {
    loadDemoEvidence(orgId);
    setLoadedAt(demoLoadedAt(orgId));
  }, [orgId]);

  const clear = useCallback(() => {
    clearDemoEvidence(orgId);
    setLoadedAt(null);
  }, [orgId]);

  return {
    loaded: loadedAt !== null,
    loadedAt,
    assets: loadedAt !== null ? demoAssets(loadedAt) : [],
    load,
    clear,
  };
}
